import { z } from 'zod';
import { idArray } from './shared.schema.ts';

export const previewAutoInvoiceReportFormatSchema = z.enum(['csv', 'pdf']);

export const previewAutoInvoiceSchema = z
	.object({
		parkingPeriodStartMonth: z.number().int().min(0).max(11).optional(),
		parkingPeriodStartYear: z.number().int().min(2021).max(2030).optional(),
		parkingPeriodEndMonth: z.number().int().min(0).max(11).optional(),
		parkingPeriodEndYear: z.number().int().min(2021).max(2030).optional(),
		areaIds: idArray.optional(),
		branchIds: idArray.optional(),
		districtIds: idArray.optional(),
		facilityIds: z.array(z.string()).optional(),
		facilityNumber: z.union([z.string(), z.number()]).optional(),
		format: previewAutoInvoiceReportFormatSchema.optional(),
	})
	.superRefine((value, ctx) => {
		const hasParkingPeriodStart =
			value.parkingPeriodStartMonth !== undefined && value.parkingPeriodStartYear !== undefined;

		if (!hasParkingPeriodStart) {
			ctx.addIssue({
				code: 'custom',
				path: ['invoiceStartDate'],
				message: 'Parking period start month and year are required',
			});
		}

		if (
			(value.parkingPeriodEndMonth !== undefined || value.parkingPeriodEndYear !== undefined) &&
			!hasParkingPeriodStart
		) {
			ctx.addIssue({
				code: 'custom',
				path: ['parkingPeriodEndMonth'],
				message: 'Cannot specify a parking period end without a parking period start',
			});
		}

		if (value.parkingPeriodEndMonth !== undefined && value.parkingPeriodEndYear === undefined) {
			ctx.addIssue({
				code: 'custom',
				path: ['parkingPeriodEndYear'],
				message: 'parkingPeriodEndYear is required when parkingPeriodEndMonth is provided',
			});
		}

		if (value.parkingPeriodEndYear !== undefined && value.parkingPeriodEndMonth === undefined) {
			ctx.addIssue({
				code: 'custom',
				path: ['parkingPeriodEndMonth'],
				message: 'parkingPeriodEndMonth is required when parkingPeriodEndYear is provided',
			});
		}

		if (
			value.parkingPeriodStartMonth !== undefined &&
			value.parkingPeriodStartYear !== undefined &&
			value.parkingPeriodEndMonth !== undefined &&
			value.parkingPeriodEndYear !== undefined
		) {
			const startValue = value.parkingPeriodStartYear * 12 + value.parkingPeriodStartMonth;
			const endValue = value.parkingPeriodEndYear * 12 + value.parkingPeriodEndMonth;

			if (endValue < startValue) {
				ctx.addIssue({
					code: 'custom',
					path: ['parkingPeriodEndMonth'],
					message: 'Parking period end must be on or after parking period start',
				});
			}
		}
	});

export type PreviewAutoInvoiceParameters = z.infer<typeof previewAutoInvoiceSchema>;
export type PreviewAutoInvoiceReportFormat = z.infer<typeof previewAutoInvoiceReportFormatSchema>;
